/* Storefront navigation. Native Eurus search, logo, cart and drawer remain in place. */
(() => {
  if (customElements.get('sami-navigation')) return;
  customElements.define('sami-navigation', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.controller = new AbortController();
      const signal = this.controller.signal;
      if (this.classList.contains('sn-mobile')) {
        this.addEventListener('toggle', event => {
          const summary = event.target.querySelector(':scope > summary');
          if (summary) summary.setAttribute('aria-expanded', String(event.target.open));
        }, {capture:true, signal});
        const drawer = this.closest('#mobile-navigation');
        const trigger = () => document.getElementById('sami-mobile-menu-trigger');
        const close = () => {
          window.Alpine?.store('xMobileNav')?.close();
          trigger()?.focus();
        };
        const focusable = () => [...drawer.querySelectorAll('a[href],button,input,select,summary,[tabindex]:not([tabindex="-1"])')]
          .filter(el => !el.disabled && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
        const changed = () => {
          const open = drawer.classList.contains('menu-opening');
          if (open && !this.drawerOpen) {
            this.querySelector('.sn-mobile-shop').open = true;
            drawer.querySelector('button[aria-label="Close menu"]')?.focus({preventScroll:true});
          } else if (!open && this.drawerOpen && drawer.contains(document.activeElement)) {
            trigger()?.focus();
          }
          this.drawerOpen = open;
        };
        this.drawerObserver = new MutationObserver(changed);
        this.drawerObserver.observe(drawer, {attributes:true,attributeFilter:['class']});
        document.addEventListener('keydown', event => {
          if (!this.drawerOpen) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
          } else if (event.key === 'Tab') {
            const items = focusable(), first = items[0], last = items[items.length-1];
            if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
              event.preventDefault(); last?.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
              event.preventDefault(); first?.focus();
            }
          }
        }, {capture:true,signal});
        changed();
        return;
      }
      this.toggles = [...this.querySelectorAll('[data-sn-toggle]')];
      this.categories = [...this.querySelectorAll('[data-sn-category]')];
      this.giftTabs = [...this.querySelectorAll('[data-sn-gift]')];
      this.addEventListener('click', event => {
        const toggle = event.target.closest('[data-sn-toggle]');
        if (toggle) this.open(toggle.dataset.snToggle, toggle.getAttribute('aria-expanded') !== 'true');
        const category = event.target.closest('[data-sn-category]');
        if (category) this.select(category.dataset.snCategory);
        const gift = event.target.closest('[data-sn-gift]');
        if (gift) this.selectGift(gift.dataset.snGift);
      }, {signal});
      this.addEventListener('pointerover', event => {
        const gift = event.target.closest('[data-sn-gift]');
        if (gift && !gift.contains(event.relatedTarget)) this.selectGift(gift.dataset.snGift);
      }, {signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          const active = this.toggles.find(t => t.getAttribute('aria-expanded') === 'true');
          this.close();
          if (active) active.focus();
          event.preventDefault();
        }
        const toggle = event.target.closest('[data-sn-toggle]');
        if (toggle && event.key === 'ArrowDown') {
          event.preventDefault();
          this.open(toggle.dataset.snToggle, true);
          this.querySelector(`#sn-${toggle.dataset.snToggle} a, #sn-${toggle.dataset.snToggle} button`)?.focus();
        }
        const gift = event.target.closest('[data-sn-gift]');
        if (gift && ['ArrowDown','ArrowUp','Home','End','ArrowRight'].includes(event.key)) {
          event.preventDefault();
          if (event.key === 'ArrowRight') {
            this.querySelector(`#sn-gift-content-${gift.dataset.snGift} a`)?.focus();
            return;
          }
          let i = this.giftTabs.indexOf(gift);
          i = event.key === 'Home' ? 0 : event.key === 'End' ? this.giftTabs.length-1 :
            (i+(event.key==='ArrowDown'?1:-1)+this.giftTabs.length)%this.giftTabs.length;
          this.selectGift(this.giftTabs[i].dataset.snGift);
          this.giftTabs[i].focus();
        }
        const category = event.target.closest('[data-sn-category]');
        if (category && ['ArrowDown','ArrowUp','Home','End','ArrowRight'].includes(event.key)) {
          event.preventDefault();
          let i = this.categories.indexOf(category);
          if (event.key === 'ArrowRight') {
            this.querySelector(`#sn-category-${category.dataset.snCategory} a`)?.focus();
            return;
          }
          i = event.key === 'Home' ? 0 : event.key === 'End' ? this.categories.length-1 :
            (i+(event.key==='ArrowDown'?1:-1)+this.categories.length)%this.categories.length;
          this.select(this.categories[i].dataset.snCategory);
          this.categories[i].focus();
        }
      }, {signal});
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close();
      }, {signal});
      document.addEventListener('focusin', event => {
        if (!this.contains(event.target)) this.close();
      }, {signal});
      window.addEventListener('resize', () => {
        if (window.innerWidth < 1024) this.close();
        this.measure();
      }, {signal});
      window.addEventListener('scroll', () => this.measure(), {passive:true, signal});
      this.select('0');
      this.selectGift('guide');
    }
    disconnectedCallback() { this.controller?.abort(); this.drawerObserver?.disconnect(); }
    measure() {
      const header = this.closest('#sticky-header-content');
      if (header) this.style.setProperty('--sn-header-bottom', `${Math.max(0,header.getBoundingClientRect().bottom)}px`);
    }
    open(name, show) {
      this.measure();
      this.toggles.forEach(toggle => {
        const on = show && toggle.dataset.snToggle === name;
        toggle.setAttribute('aria-expanded', String(on));
        this.querySelector(`#sn-${toggle.dataset.snToggle}`).hidden = !on;
      });
    }
    close() { this.open('', false); }
    selectGift(id) {
      this.giftTabs.forEach(tab => {
        const on = tab.dataset.snGift === id;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        this.querySelector(`#sn-gift-content-${tab.dataset.snGift}`).hidden = !on;
      });
    }
    select(id) {
      this.categories.forEach(category => {
        const on = category.dataset.snCategory === id;
        category.setAttribute('aria-selected', String(on));
        category.tabIndex = on ? 0 : -1;
        this.querySelector(`#sn-category-${category.dataset.snCategory}`).hidden = !on;
      });
    }
  });
})();
